import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/components/dispatch/utils";

/**
 * Fire MDT — apparatus terminal interface.
 *
 * Terminal identity is established by the backend's IP-mapping endpoint
 * (`/api/fire-mdt/state`). On mount we ask "who are you?"; if the
 * client IP is bound to a Fire unit, the page loads pre-authenticated.
 * If not, we show a one-time bind selector so the dispatcher (or first-on
 * crew member) can permanently associate this terminal with an apparatus.
 *
 * The page subscribes to the dispatch WebSocket for realtime updates —
 * any unit_updated / job_updated affecting THIS unit triggers a refetch
 * of `/api/fire-mdt/state`, so a fresh dispatch from the console
 * populates the incident card within ~100ms.
 *
 * Visual reference: operator-supplied screenshot — blue title bar, grey
 * action grid, colored LED bars under each status button.
 */

// Primary status buttons — 3 rows of 4, per operator spec iter 75.
// Rows 1 & 2 are direct status changes. Row 3 starts with the
// signal-activation expander button (10-) which opens a sub-menu of
// 10-25..10-43 codes; the rest of row 3 is availability + alarm codes.
//
// `code` is the value POSTed to /fire-mdt/status. `supvOnly` cells are
// hidden for non-supervisor apparatus per spec (10-12). `expander`
// indicates the cell opens the Signal Activation submenu instead of
// firing a direct status change.
const STATUS_BUTTON_GRID = [
  // Row 1
  [
    { code: "RESP",  label: "RESP",  sub: "RESPONDING TO BOX", led: "#33CC33" },
    { code: "10-84", label: "10-84", sub: "ON SCENE",          led: "#FF3B30" },
    { code: "10-12", label: "10-12", sub: "PRELIM REPORT",     led: "#FFCC00", supvOnly: true },
    { code: "10-20", label: "10-20", sub: "SLOW DOWN",         led: "#FF9500" },
  ],
  // Row 2
  [
    { code: "10-18", label: "10-18", sub: "1 ENG + 1 LADDER",  led: "#FFCC00" },
    { code: "10-19", label: "10-19", sub: "1 ENG OR 1 LADDER", led: "#FFCC00" },
    { code: "10-91", label: "10-91", sub: "EMS ONLY",          led: "#FFCC00" },
    { code: "10-92", label: "10-92", sub: "MALICIOUS FALSE",   led: "#FFCC00" },
  ],
  // Row 3 — signal-activation expander (10-) + 3 placeholders (the
  // expander opens the submenu; one cell is reserved for AVAILABLE 10-8).
  [
    { code: "10-",   label: "10-",   sub: "SIGNAL ACTIVATION", led: "#33CC33", expander: true },
    { code: "10-8",  label: "10-8",  sub: "AVAILABLE",         led: "#33CC33" },
    null,
    null,
  ],
];

// Signal-activation submenu — third-row expander. Each parent code may
// have child sub-codes; if `subs` is empty, pressing the parent posts a
// "no-code" signal directly. Bold means it has a sub-menu.
const SIGNAL_ACTIVATION_TREE = [
  { code: "10-25", label: "10-25", desc: "Manhole / Transformer Vault Fire", subs: [
    { code: "",        label: "NO CODE — Other" },
    { code: "CODE 1",  label: "CODE 1 — Fire extended into building" },
    { code: "CODE 2",  label: "CODE 2 — Covers blown / pressure" },
    { code: "CODE 3",  label: "CODE 3 — Smoke from manhole" },
    { code: "CODE 4",  label: "CODE 4 — Transformer fire" },
  ]},
  { code: "10-26", label: "10-26", desc: "Food on Stove" },
  { code: "10-27", label: "10-27", desc: "Compactor / Incinerator Fire" },
  { code: "10-28", label: "10-28", desc: "Subway / Railroad", subs: [
    { code: "CODE 1", label: "CODE 1 — Structural fire" },
    { code: "CODE 2", label: "CODE 2 — Non-structural fire" },
    { code: "CODE 3", label: "CODE 3 — Non-medical emergency" },
  ]},
  { code: "10-29", label: "10-29", desc: "Elevator", subs: [
    { code: "CODE 1", label: "CODE 1 — Occupied" },
    { code: "CODE 2", label: "CODE 2 — Unoccupied" },
  ]},
  { code: "10-31", label: "10-31", desc: "Assist Civilian (Non-Medical)" },
  { code: "10-32", label: "10-32", desc: "Defective Oil Burner" },
  { code: "10-33", label: "10-33", desc: "Odor of Smoke", subs: [
    { code: "CODE 1", label: "CODE 1 — External source" },
    { code: "CODE 2", label: "CODE 2 — Other" },
  ]},
  { code: "10-34", label: "10-34", desc: "Sprinkler", subs: [
    { code: "CODE 1", label: "CODE 1 — Defective" },
    { code: "CODE 2", label: "CODE 2 — Unwarranted" },
    { code: "CODE 3", label: "CODE 3 — Heat activated" },
  ]},
  { code: "10-35", label: "10-35", desc: "Unwarranted Alarm", subs: [
    { code: "",       label: "NO CODE — Unwarranted" },
    { code: "CODE 1", label: "CODE 1 — Testing" },
    { code: "CODE 2", label: "CODE 2 — Construction" },
    { code: "CODE 3", label: "CODE 3 — Household" },
    { code: "CODE 4", label: "CODE 4 — Other" },
  ]},
  { code: "10-36", label: "10-36", desc: "Auto Incident", subs: [
    { code: "CODE 1", label: "CODE 1 — Washdown" },
    { code: "CODE 2", label: "CODE 2 — No injuries" },
    { code: "CODE 3", label: "CODE 3 — Injuries" },
    { code: "CODE 4", label: "CODE 4 — Extrication" },
  ]},
  { code: "10-37", label: "10-37", desc: "Medical (Non-Fire)", subs: [
    { code: "CODE 1", label: "CODE 1 — Deceased" },
    { code: "CODE 2", label: "CODE 2 — Not breathing" },
    { code: "CODE 3", label: "CODE 3 — Ill / Injured" },
    { code: "CODE 4", label: "CODE 4 — No contact" },
  ]},
  { code: "10-38", label: "10-38", desc: "Carbon Monoxide", subs: [
    { code: "CODE 1", label: "CODE 1 — Detector issue" },
    { code: "CODE 2", label: "CODE 2 — 1–9 PPM" },
    { code: "CODE 3", label: "CODE 3 — >9 PPM" },
    { code: "CODE 4", label: "CODE 4 — No activation" },
  ]},
  { code: "10-39", label: "10-39", desc: "Standby" },
  { code: "10-40", label: "10-40", desc: "Utility", subs: [
    { code: "CODE 1", label: "CODE 1 — Gas" },
    { code: "CODE 2", label: "CODE 2 — Electric" },
    { code: "CODE 3", label: "CODE 3 — Water" },
    { code: "CODE 4", label: "CODE 4 — Steam" },
  ]},
  { code: "10-41", label: "10-41", desc: "Suspicious Fire", subs: [
    { code: "CODE 1", label: "CODE 1 — Occupied" },
    { code: "CODE 2", label: "CODE 2 — Unoccupied building" },
    { code: "CODE 3", label: "CODE 3 — Unoccupied vehicle" },
    { code: "CODE 4", label: "CODE 4 — Vacant structure" },
  ]},
  { code: "10-42", label: "10-42", desc: "Downed Tree" },
  { code: "10-43", label: "10-43", desc: "Non-Fire Rescue" },
];

// Side panel — operator spec: 4 buttons + LOG OFF (red, replaces CLEAR FORM).
// HOUSE STATUS + MSG TO DISP are SUPV-only.
const SIDE_PANEL = [
  { id: "unit_status",       label: "UNIT STATUS" },
  { id: "incident_history",  label: "INCIDENT HISTORY" },
  { id: "incident_summary",  label: "INCIDENT SUMMARY" },
  { id: "house_status",      label: "HOUSE STATUS",  supvOnly: true },
  { id: "msg_to_disp",       label: "MSG TO DISP",   supvOnly: true },
  { id: "logoff",            label: "LOG OFF",       danger: true },  // replaces CLEAR FORM
];

const FOOTER_BUTTONS = ["PRINT", "SP SAVE", "SP RCL", "NEXT", "PREV", "CLEAR", "ERASE"];

export default function FireMDTPage() {
  // Identity & binding state
  const [identifying, setIdentifying] = useState(true);
  const [bound, setBound] = useState(false);
  const [unit, setUnit] = useState(null);
  const [ipAddress, setIpAddress] = useState("");
  const [bindError, setBindError] = useState("");
  // Bind-selector UI state
  const [fireUnits, setFireUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");

  // Operational state
  const [activeIncident, setActiveIncident] = useState(null);
  const [roster, setRoster] = useState(null);
  const [showRoster, setShowRoster] = useState(false);
  const [stripText, setStripText] = useState("");
  const [stats, setStats] = useState({ saved: 0, stored: 0, waiting: 0 });
  // Iter 75 spec — Signal Activation submenu (third row).
  // null = closed; "primary" = parent grid; { code, desc, subs } = sub-codes.
  const [signalMenu, setSignalMenu] = useState(null);
  // Iter 75 spec — Incident Summary panel (side button 3).
  const [showIncidentSummary, setShowIncidentSummary] = useState(false);
  // Iter 75 spec — LOG OFF confirmation modal.
  const [confirmLogoff, setConfirmLogoff] = useState(false);
  // Iter 76 — INCIDENT HISTORY / HOUSE STATUS / MSG TO DISP feeds.
  const [incHistory, setIncHistory] = useState(null);
  const [showIncHistory, setShowIncHistory] = useState(false);
  const [houseStatus, setHouseStatus] = useState(null);
  const [showHouseStatus, setShowHouseStatus] = useState(false);
  const [showMsgToDisp, setShowMsgToDisp] = useState(false);
  const [msgRoster, setMsgRoster] = useState([]);          // [{type,id,name}]
  const [msgRecipients, setMsgRecipients] = useState([]);  // selected ids
  const [msgBody, setMsgBody] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgSuccess, setMsgSuccess] = useState("");

  // Iter 79 — LOGON form state (used when bound but unit.logged_in=false).
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [logonChauffeur, setLogonChauffeur] = useState("");
  const [logonOfficer, setLogonOfficer] = useState("");
  const [logonNozzle, setLogonNozzle] = useState("");
  const [logonBackup, setLogonBackup] = useState("");
  const [logonControl, setLogonControl] = useState("");
  const [logonRadio1, setLogonRadio1] = useState("");
  const [logonRadio2, setLogonRadio2] = useState("");
  const [logonRadio3, setLogonRadio3] = useState("");
  const [logonRadio4, setLogonRadio4] = useState("");
  const [logonRadio5, setLogonRadio5] = useState("");
  const [logonTour, setLogonTour] = useState("Day");
  const [logonFrom, setLogonFrom] = useState("07:00");
  const [logonTo, setLogonTo] = useState("15:00");
  const [logonError, setLogonError] = useState("");
  const [logonSubmitting, setLogonSubmitting] = useState(false);
  const [, setTick] = useState(0);
  const wsRef = useRef(null);
  const unitIdRef = useRef(null);

  // Supervisor gating — operator spec: 10-12 + HOUSE STATUS + MSG TO DISP
  // are SUPV-only. Non-supervisor apparatus see those cells blank.
  const isSupervisor = useMemo(() => {
    const ut = (unit?.unit_type || "").toUpperCase();
    return /SUPERVISOR|BATTALION|CHIEF|DEPUTY/.test(ut);
  }, [unit]);

  // Refresh clock every second so date in title bar stays current.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const identify = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/fire-mdt/state`);
      setIpAddress(r.data?.ip_address || "");
      if (r.data?.bound && r.data?.unit) {
        setBound(true);
        setUnit(r.data.unit);
        unitIdRef.current = r.data.unit.id;
        setActiveIncident(r.data.incident || null);
        setRoster(r.data.roster || null);
        // Persist locally so a hard reset / power loss can re-bind even if
        // the apparatus's IP changed (DHCP re-lease, network reconfig).
        try {
          localStorage.setItem("fire_mdt_session", JSON.stringify({
            unit_id: r.data.unit.id,
            unit_name: r.data.unit.name,
          }));
        } catch {}
      } else {
        // Server says we're not IP-bound. Per operator spec (localStorage
        // first, IP fallback) — try to re-bind from the locally-saved
        // unit_id before falling back to the bind selector.
        let recovered = false;
        try {
          const saved = JSON.parse(localStorage.getItem("fire_mdt_session") || "null");
          if (saved?.unit_id) {
            const re = await axios.post(`${API}/fire-mdt/bind`, { unit_id: saved.unit_id });
            if (!re.data?.error) {
              const r2 = await axios.get(`${API}/fire-mdt/state`);
              if (r2.data?.bound && r2.data?.unit) {
                setBound(true);
                setUnit(r2.data.unit);
                unitIdRef.current = r2.data.unit.id;
                setActiveIncident(r2.data.incident || null);
                setRoster(r2.data.roster || null);
                setIpAddress(r2.data?.ip_address || "");
                recovered = true;
              }
            } else {
              // Saved unit no longer Fire / no longer exists — drop the snapshot.
              localStorage.removeItem("fire_mdt_session");
            }
          }
        } catch { /* fall through to bind selector */ }
        if (!recovered) {
          setBound(false);
          setUnit(null);
          setActiveIncident(null);
          setRoster(null);
          unitIdRef.current = null;
        }
      }
    } catch (e) {
      setBindError("Could not contact dispatch server");
    } finally {
      setIdentifying(false);
    }
  }, []);

  const fetchFireUnits = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/units`);
      const FIRE = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF"]);
      setFireUnits((r.data || []).filter((u) => FIRE.has(((u.unit_type || "")).toUpperCase())));
    } catch {
      setFireUnits([]);
    }
  }, []);

  useEffect(() => {
    identify();
  }, [identify]);

  useEffect(() => {
    if (!bound) fetchFireUnits();
  }, [bound, fetchFireUnits]);

  // Iter 79 — when bound but the apparatus isn't logged on yet, pull
  // the agency's personnel for the position-name dropdowns.
  useEffect(() => {
    if (!bound || unit?.logged_in || !unit?.agency) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/users`);
        const inAgency = (r.data || []).filter((u) => u.agency === unit.agency);
        if (alive) setAgencyUsers(inAgency);
      } catch {
        if (alive) setAgencyUsers([]);
      }
    })();
    return () => { alive = false; };
  }, [bound, unit?.logged_in, unit?.agency]);

  const submitFireLogon = async () => {
    setLogonError("");
    if (!unit?.id) return;
    if (!logonChauffeur || !logonOfficer) {
      setLogonError("Chauffeur and Officer are required.");
      return;
    }
    // Reject duplicate person across positions.
    const positions = [logonChauffeur, logonOfficer, logonNozzle, logonBackup, logonControl].filter(Boolean);
    if (new Set(positions).size !== positions.length) {
      setLogonError("Same person can't fill two positions.");
      return;
    }
    setLogonSubmitting(true);
    try {
      const r = await axios.post(`${API}/fire-mdt/logon`, {
        unit_id: unit.id,
        chauffeur: logonChauffeur,
        officer: logonOfficer,
        nozzle: logonNozzle,
        backup: logonBackup,
        control: logonControl,
        radio_1: logonRadio1,
        radio_2: logonRadio2,
        radio_3: logonRadio3,
        radio_4: logonRadio4,
        radio_5: logonRadio5,
        tour: logonTour,
        service_time_from: logonFrom,
        service_time_to: logonTo,
      });
      if (r.data?.error) {
        setLogonError(r.data.error);
      } else {
        // Success — refetch state so we drop into the main MDT screen.
        await identify();
        setStripText("LOGGED ON — apparatus in service");
      }
    } catch (e) {
      setLogonError(e?.response?.data?.error || "Logon failed");
    } finally {
      setLogonSubmitting(false);
    }
  };

  // ─── WebSocket — refetch state on any unit/job update affecting us ───
  // The Fire MDT subscribes to the same broadcast channel as the dispatcher
  // console. We only act on messages whose unit_id / current_job_id matches
  // our bound unit; anything else is ignored. This delivers an incident
  // card within ~100ms of the dispatcher pressing ASSIGN.
  useEffect(() => {
    if (!bound) return undefined;
    let ws = null;
    let alive = true;
    let reconnectTimer = null;

    const connect = () => {
      const wsUrl = process.env.REACT_APP_BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://") + "/api/ws";
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          const myId = unitIdRef.current;
          if (!myId) return;
          if (msg.type === "unit_updated" && msg.unit?.id === myId) {
            // Our unit changed (status, crew, current_job_id, etc.) — refetch.
            identify();
          } else if (msg.type === "job_updated" && msg.job?.id && activeIncident?.id === msg.job.id) {
            setActiveIncident(msg.job);
          } else if (msg.type === "job_created" || msg.type === "job_assigned") {
            // Could be us — easiest to refetch.
            identify();
          }
        } catch {}
      };
      ws.onclose = () => {
        if (!alive) return;
        reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    };
    connect();
    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) try { ws.close(); } catch {}
    };
  }, [bound, identify, activeIncident?.id]);

  const handleBind = async () => {
    if (!selectedUnitId) {
      setBindError("Pick an apparatus first");
      return;
    }
    setBindError("");
    try {
      const r = await axios.post(`${API}/fire-mdt/bind`, { unit_id: selectedUnitId });
      if (r.data?.error) {
        setBindError(r.data.error);
        return;
      }
      // Re-identify so the page picks up the new binding cleanly.
      await identify();
    } catch (e) {
      setBindError(e?.response?.data?.error || "Bind failed");
    }
  };

  const handleSwitchApparatus = async () => {
    try {
      await axios.delete(`${API}/fire-mdt/bind`);
    } finally {
      // Clear local snapshot too — operator is explicitly asking to detach
      // this terminal from the apparatus.
      try { localStorage.removeItem("fire_mdt_session"); } catch {}
      setBound(false);
      setUnit(null);
      setActiveIncident(null);
      setSelectedUnitId("");
      fetchFireUnits();
    }
  };

  const sendStatus = async (code) => {
    if (!unit?.id) return;
    setStripText(`Sending ${code}...`);
    try {
      const r = await axios.post(`${API}/fire-mdt/status`, { unit_id: unit.id, code });
      if (r.data?.error) {
        setStripText(`ERR: ${r.data.error}`);
        return;
      }
      setStripText(`${code} — ${r.data.label || ""}`);
      // 10-8 transitions the apparatus into service. Clear any active incident
      // panel since we're free.
      if (code === "10-8") setActiveIncident(null);
    } catch (e) {
      setStripText(`ERR: ${e?.response?.data?.error || "send failed"}`);
    }
  };

  // Signal Activation submenu — fires AFTER the operator picks a sub-code
  // (or directly for codes with no sub-codes). Posts to /fire-mdt/signal
  // which logs unit_history without changing the unit's signal.
  const sendSignal = async (parentCode, subCode = "", subLabel = "") => {
    if (!unit?.id) return;
    const display = subCode ? `${parentCode} ${subCode}` : parentCode;
    setStripText(`Sending ${display}...`);
    try {
      const r = await axios.post(`${API}/fire-mdt/signal`, {
        unit_id: unit.id,
        code: parentCode,
        sub_code: subLabel || subCode,
      });
      if (r.data?.error) {
        setStripText(`ERR: ${r.data.error}`);
        return;
      }
      setStripText(`${display} — logged`);
      setSignalMenu(null);
    } catch (e) {
      setStripText(`ERR: ${e?.response?.data?.error || "signal failed"}`);
    }
  };

  // LOG OFF flow — replaces the old CLEAR FORM button (operator spec
  // iter 75). Confirmation modal first; on YES, post to /fire-mdt/logoff
  // which flips logged_in=false on the backend and broadcasts.
  const performLogoff = async () => {
    if (!unit?.id) return;
    setConfirmLogoff(false);
    setStripText("Logging off…");
    try {
      const r = await axios.post(`${API}/fire-mdt/logoff`, { unit_id: unit.id, code: "" });
      if (r.data?.error) {
        setStripText(`ERR: ${r.data.error}`);
        return;
      }
      // Also unbind this terminal's IP so the page returns to the bind
      // selector cleanly (otherwise /state would auto-rebind on reload).
      try { await axios.delete(`${API}/fire-mdt/bind`); } catch {}
      // Drop local session — apparatus is no longer in service.
      try { localStorage.removeItem("fire_mdt_session"); } catch {}
      setBound(false);
      setUnit(null);
      setActiveIncident(null);
      setRoster(null);
      unitIdRef.current = null;
      setSelectedUnitId("");
      fetchFireUnits();
      setStripText("LOGGED OFF");
    } catch (e) {
      setStripText(`ERR: ${e?.response?.data?.error || "logoff failed"}`);
    }
  };

  // Side panel button click handler — routes to the appropriate
  // panel/modal/action.
  const handleSidePanel = async (id) => {
    if (id === "logoff") { setConfirmLogoff(true); return; }
    if (id === "unit_status") { setShowRoster(true); setStripText("UNIT STATUS — roster open"); return; }
    if (id === "incident_summary") { setShowIncidentSummary(true); return; }
    if (id === "incident_history") {
      if (!unit?.id) return;
      setShowIncHistory(true);
      setStripText("Loading incident history…");
      try {
        const r = await axios.get(`${API}/fire-mdt/incident-history`, { params: { unit_id: unit.id } });
        setIncHistory(r.data || { current: { events: [] }, earlier: [] });
        setStripText("INCIDENT HISTORY — loaded");
      } catch {
        setIncHistory({ current: { events: [] }, earlier: [], error: "Could not load history" });
        setStripText("ERR: history load failed");
      }
      return;
    }
    if (id === "house_status") {
      if (!unit?.agency) return;
      setShowHouseStatus(true);
      setStripText("Loading house status…");
      try {
        const r = await axios.get(`${API}/fire-mdt/house-status`, { params: { agency: unit.agency } });
        setHouseStatus(r.data || null);
        setStripText("HOUSE STATUS — loaded");
      } catch {
        setHouseStatus({ error: "Could not load house status" });
        setStripText("ERR: house status load failed");
      }
      return;
    }
    if (id === "msg_to_disp") {
      setShowMsgToDisp(true);
      setMsgBody("");
      setMsgError("");
      setMsgSuccess("");
      setMsgRecipients([]);
      try {
        const r = await axios.get(`${API}/messages/online-roster`);
        const dispatchers = (r.data?.dispatchers || []).map((d) => ({
          type: "dispatcher",
          id: d.user_id,
          name: d.dispatcher_userid || d.username || "DISP",
        })).filter((d) => d.id);
        setMsgRoster(dispatchers);
      } catch {
        setMsgRoster([]);
      }
      return;
    }
    setStripText(`${id.toUpperCase().replace(/_/g, " ")} — pending`);
  };

  const sendMsgToDisp = async () => {
    if (!unit?.id) return;
    if (!msgBody.trim()) { setMsgError("Message body required"); return; }
    if (msgRecipients.length === 0) { setMsgError("Pick at least one dispatcher (or SEND ALL)"); return; }
    setMsgSending(true);
    setMsgError("");
    try {
      // SEND ALL → fan out to every dispatcher in the roster.
      const recips = msgRecipients.includes("__ALL__")
        ? msgRoster
        : msgRoster.filter((d) => msgRecipients.includes(d.id));
      if (recips.length === 0) { setMsgError("No online dispatchers"); setMsgSending(false); return; }
      const r = await axios.post(`${API}/messages`, {
        sender_type: "unit",
        sender_id: unit.id,
        sender_name: unit.name || "",
        recipients: recips,
        body: msgBody.trim(),
      });
      if (r.data?.error) {
        setMsgError(r.data.error);
      } else {
        setMsgSuccess(`Sent to ${recips.length} dispatcher${recips.length === 1 ? "" : "s"}`);
        setMsgBody("");
        setMsgRecipients([]);
      }
    } catch (e) {
      setMsgError(e?.response?.data?.error || "Send failed");
    } finally {
      setMsgSending(false);
    }
  };

  const dateStr = useMemo(() => {
    const d = new Date();
    return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()} ${d.toTimeString().slice(0, 8)}`;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Bind selector (first-time setup) ───
  if (identifying) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#202428", color: "#FFF", fontFamily: "Arial, sans-serif" }}>
        Identifying terminal…
      </div>
    );
  }
  if (!bound) {
    return (
      <div style={SHELL_STYLE} data-testid="fire-mdt-bind-screen">
        <div style={TITLE_BAR_STYLE}>FIRE MDT — BIND APPARATUS</div>
        <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
          <div style={{ fontSize: 16, color: "#FFF" }}>This terminal is not yet bound to an apparatus.</div>
          <div style={{ fontSize: 13, color: "#9AA8B8" }}>Source IP: <strong style={{ color: "#FFE0B0", fontFamily: "monospace" }}>{ipAddress || "?"}</strong></div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <select
              data-testid="fire-mdt-bind-select"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              style={{ padding: "10px 14px", fontSize: 16, minWidth: 320 }}
            >
              <option value="">Choose apparatus…</option>
              {fireUnits.map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.unit_type} ({u.agency})</option>
              ))}
            </select>
            <button
              data-testid="fire-mdt-bind-btn"
              onClick={handleBind}
              style={{ padding: "10px 24px", fontSize: 16, fontWeight: 700, background: "#1E5F9C", color: "#FFF", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              BIND TERMINAL
            </button>
          </div>
          {bindError && <div style={{ color: "#FF7070", fontSize: 13 }}>{bindError}</div>}
          <div style={{ fontSize: 11, color: "#7A8896", textAlign: "center", maxWidth: 500, marginTop: 12 }}>
            Once bound, this terminal will auto-recognize the apparatus on every reload.
            The dispatcher can re-bind from the Admin panel if the terminal moves.
          </div>
        </div>
      </div>
    );
  }

  // ─── Iter 79: LOGON screen — bound but not yet in service ───
  // Renders a position-name + radio + tour form. On submit the apparatus
  // flips to logged_in=true and shows on the dispatch board.
  if (!unit?.logged_in) {
    const personnelOptions = (
      <>
        <option value="">— select —</option>
        {agencyUsers.map((u) => (
          <option key={u.id} value={`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.id}>
            {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.id}
          </option>
        ))}
      </>
    );
    const inputStyle = { width: "100%", padding: "8px 10px", fontSize: 14, background: "#0E2942", color: "#FFF", border: "1px solid #2A6B9C", borderRadius: 3, fontFamily: "monospace", boxSizing: "border-box" };
    const labelStyle = { fontSize: 11, color: "#A0C0D8", letterSpacing: 1, fontWeight: 700, marginBottom: 4, display: "block" };
    return (
      <div style={SHELL_STYLE} data-testid="fire-mdt-logon-screen">
        <div style={TITLE_BAR_STYLE}>FIRE MDT — APPARATUS LOGON · {unit?.name} ({unit?.unit_type})</div>
        <div style={{ flex: 1, padding: 24, overflow: "auto", background: "#102540", color: "#FFF", fontFamily: "Arial, sans-serif" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div style={{ fontSize: 16, marginBottom: 16, color: "#FFD46B", fontWeight: 700 }}>
              Enter the truck positions and radio assignments to place this apparatus in service.
            </div>

            <div style={{ background: "#0A1A2E", border: "1px solid #2A6B9C", borderRadius: 4, padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FFD46B", letterSpacing: 1, marginBottom: 12 }}>POSITIONS ON THE TRUCK</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>CHAUFFEUR <span style={{ color: "#FF7070" }}>*</span></label>
                  <select data-testid="fire-mdt-logon-chauffeur" style={inputStyle} value={logonChauffeur} onChange={(e) => setLogonChauffeur(e.target.value)}>{personnelOptions}</select>
                </div>
                <div>
                  <label style={labelStyle}>OFFICER <span style={{ color: "#FF7070" }}>*</span></label>
                  <select data-testid="fire-mdt-logon-officer" style={inputStyle} value={logonOfficer} onChange={(e) => setLogonOfficer(e.target.value)}>{personnelOptions}</select>
                </div>
                <div>
                  <label style={labelStyle}>NOZZLE</label>
                  <select data-testid="fire-mdt-logon-nozzle" style={inputStyle} value={logonNozzle} onChange={(e) => setLogonNozzle(e.target.value)}>{personnelOptions}</select>
                </div>
                <div>
                  <label style={labelStyle}>BACKUP</label>
                  <select data-testid="fire-mdt-logon-backup" style={inputStyle} value={logonBackup} onChange={(e) => setLogonBackup(e.target.value)}>{personnelOptions}</select>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>CONTROL</label>
                  <select data-testid="fire-mdt-logon-control" style={inputStyle} value={logonControl} onChange={(e) => setLogonControl(e.target.value)}>{personnelOptions}</select>
                </div>
              </div>
            </div>

            <div style={{ background: "#0A1A2E", border: "1px solid #2A6B9C", borderRadius: 4, padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FFD46B", letterSpacing: 1, marginBottom: 12 }}>RADIOS</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
                {[
                  ["RADIO 1", logonRadio1, setLogonRadio1, "fire-mdt-logon-radio-1"],
                  ["RADIO 2", logonRadio2, setLogonRadio2, "fire-mdt-logon-radio-2"],
                  ["RADIO 3", logonRadio3, setLogonRadio3, "fire-mdt-logon-radio-3"],
                  ["RADIO 4", logonRadio4, setLogonRadio4, "fire-mdt-logon-radio-4"],
                  ["RADIO 5", logonRadio5, setLogonRadio5, "fire-mdt-logon-radio-5"],
                ].map(([lab, val, set, tid]) => (
                  <div key={tid}>
                    <label style={labelStyle}>{lab}</label>
                    <input data-testid={tid} style={inputStyle} value={val} onChange={(e) => set(e.target.value.toUpperCase())} placeholder="R-101" />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "#0A1A2E", border: "1px solid #2A6B9C", borderRadius: 4, padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FFD46B", letterSpacing: 1, marginBottom: 12 }}>TOUR</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>TOUR</label>
                  <select data-testid="fire-mdt-logon-tour" style={inputStyle} value={logonTour} onChange={(e) => setLogonTour(e.target.value)}>
                    <option value="Day">Day</option>
                    <option value="Night">Night</option>
                    <option value="Overnight">Overnight</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>FROM</label>
                  <input data-testid="fire-mdt-logon-from" style={inputStyle} value={logonFrom} onChange={(e) => setLogonFrom(e.target.value)} placeholder="07:00" />
                </div>
                <div>
                  <label style={labelStyle}>TO</label>
                  <input data-testid="fire-mdt-logon-to" style={inputStyle} value={logonTo} onChange={(e) => setLogonTo(e.target.value)} placeholder="15:00" />
                </div>
              </div>
            </div>

            {logonError && (
              <div data-testid="fire-mdt-logon-error" style={{ background: "#3A0E0E", color: "#FF8A8A", padding: 10, borderRadius: 3, marginBottom: 14, fontSize: 13, fontWeight: 700 }}>
                {logonError}
              </div>
            )}

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                data-testid="fire-mdt-logon-switch"
                onClick={handleSwitchApparatus}
                style={{ padding: "12px 20px", fontSize: 13, fontWeight: 700, background: "#444", color: "#FFF", border: "none", borderRadius: 3, cursor: "pointer" }}
              >SWITCH APPARATUS</button>
              <button
                data-testid="fire-mdt-logon-submit"
                disabled={logonSubmitting}
                onClick={submitFireLogon}
                style={{ padding: "12px 36px", fontSize: 16, fontWeight: 700, background: logonSubmitting ? "#888" : "#1B7A36", color: "#FFF", border: "none", borderRadius: 3, cursor: logonSubmitting ? "default" : "pointer", letterSpacing: 1 }}
              >{logonSubmitting ? "LOGGING ON…" : "LOG ON"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main MDT screen ───
  return (
    <div style={SHELL_STYLE} data-testid="fire-mdt-main">
      {/* Title bar — agency name on left, red panic indicator on right */}
      <div style={{ ...TITLE_BAR_STYLE, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span data-testid="fire-mdt-title-agency">{unit?.agency || ""}</span>
        <div style={{ width: 48, height: 24, background: "#D8252B" }} />
      </div>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 360px", gap: 8, padding: 8, overflow: "hidden" }}>
        {/* LEFT — incident card */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "hidden" }}>
          <div style={INCIDENT_CARD_STYLE} data-testid="fire-mdt-incident">
            <div style={{ fontSize: 18, fontWeight: 700 }}>{unit?.name} — {unit?.unit_type}</div>
            {activeIncident ? (
              <>
                <div style={{ marginTop: 10, fontSize: 16 }} data-testid="fire-mdt-incident-call-type">
                  {activeIncident.call_type || "—"}
                  {activeIncident.priority ? <span style={{ marginLeft: 12, color: "#FFD46B" }}>PRI {activeIncident.priority}</span> : null}
                </div>
                <div style={{ marginTop: 10 }} data-testid="fire-mdt-incident-box">
                  BOX: {activeIncident.box || activeIncident.pct || "—"}
                </div>
                <div style={{ marginTop: 4 }} data-testid="fire-mdt-incident-location">
                  {activeIncident.location || ""}{activeIncident.apt ? ` APT ${activeIncident.apt}` : ""}
                </div>
                {(activeIncident.cross_low || activeIncident.cross_high) && (
                  <div style={{ marginTop: 4, fontSize: 14, color: "#CFE0F0" }} data-testid="fire-mdt-incident-cross">
                    XS: {activeIncident.cross_low || "—"} / {activeIncident.cross_high || "—"}
                    {activeIncident.cross_special ? ` (${activeIncident.cross_special})` : ""}
                  </div>
                )}
                <div style={{ marginTop: 8 }} data-testid="fire-mdt-incident-cad">
                  INCIDENT#: {activeIncident.cad_number || "—"}
                </div>
                {(activeIncident.cids_data && activeIncident.cids_data.length > 0) && (
                  <div style={{ marginTop: 8, padding: 8, background: "rgba(0,0,0,0.25)", borderRadius: 3 }} data-testid="fire-mdt-incident-cids">
                    <div style={{ fontSize: 12, color: "#FFD46B", letterSpacing: 1 }}>CIDS</div>
                    {activeIncident.cids_data.map((c, i) => (
                      <div key={i} style={{ fontSize: 13, marginTop: 2 }}>{c}</div>
                    ))}
                  </div>
                )}
                {(activeIncident.location_data && activeIncident.location_data.length > 0) && (
                  <div style={{ marginTop: 8, padding: 8, background: "rgba(0,0,0,0.18)", borderRadius: 3 }}>
                    <div style={{ fontSize: 12, color: "#A0C0D8", letterSpacing: 1 }}>LOCATION DATA</div>
                    {activeIncident.location_data.map((c, i) => (
                      <div key={i} style={{ fontSize: 13, marginTop: 2 }}>{c}</div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ marginTop: 12, fontStyle: "italic", color: "#CFE0F0" }}>
                  CTRL ISL — APPARATUS AVAILABLE
                </div>
                <div style={{ marginTop: 6 }}>* RESPOND TO ANY DISPATCH *</div>
                <div style={{ marginTop: 4, fontSize: 13, color: "#A0C0D8" }}>No active incident. Press 10-8 if returning to service.</div>
              </>
            )}
            <div style={{ marginTop: 16, fontSize: 14, color: "#88A0B8" }}>
              ************* {dateStr} *************
            </div>
          </div>

          {/* Status strip — text input + counters per reference */}
          <div style={STATUS_STRIP_STYLE}>
            <input
              data-testid="fire-mdt-strip-input"
              value={stripText}
              onChange={(e) => setStripText(e.target.value)}
              placeholder="0"
              style={{ flex: 1, background: "transparent", border: "none", color: "#33FF33", fontFamily: "Courier New, monospace", fontSize: 18, fontWeight: 700, outline: "none" }}
            />
            <span style={{ color: "#33FF33", fontFamily: "Courier New, monospace", fontWeight: 700, marginLeft: 16 }}>
              {stats.saved} SAVED
            </span>
            <span style={{ color: "#33FF33", fontFamily: "Courier New, monospace", fontWeight: 700, marginLeft: 16 }}>
              {stats.stored} STORED
            </span>
            <span style={{ color: "#33FF33", fontFamily: "Courier New, monospace", fontWeight: 700, marginLeft: 16 }}>
              {stats.waiting} WAITING
            </span>
          </div>

          {/* Status button grid — operator spec iter 75: 3 rows × 4 cols.
              Row 3 starts with the 10- "Signal Activation" expander which
              opens the submenu of 10-25..10-43 codes. supvOnly cells render
              as a blank placeholder for non-supervisor apparatus.

              Iter 81 — when the apparatus is not assigned to an incident
              (no current_job_id), only 10-8 stays enabled. All other status
              buttons (RESP / 10-84 / 10-12 / 10-20 / 10-18 / 10-19 / 10-91
              / 10-92 / 10- expander) are disabled to prevent the crew from
              accidentally posting status without a CAD job in context. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {STATUS_BUTTON_GRID.flat().map((b, i) => {
              if (!b) return <div key={`spacer-${i}`} />;
              if (b.supvOnly && !isSupervisor) return <div key={`hidden-${i}`} />;
              const onClick = b.expander
                ? () => setSignalMenu({ stage: "primary" })
                : () => sendStatus(b.code);
              const noIncident = !unit?.current_job_id;
              const isDisabled = noIncident && b.code !== "10-8";
              return (
                <button
                  key={b.code}
                  data-testid={`fire-mdt-status-${b.code.replace(/-/g, "_").replace(/\s+/g, "_")}`}
                  onClick={onClick}
                  disabled={isDisabled}
                  style={{
                    ...GRID_BTN_STYLE,
                    opacity: isDisabled ? 0.35 : 1,
                    cursor: isDisabled ? "not-allowed" : GRID_BTN_STYLE.cursor || "pointer",
                    filter: isDisabled ? "grayscale(0.7)" : undefined,
                  }}
                >
                  <span style={{ fontSize: 22, fontWeight: 700, color: "#202428" }}>{b.label}</span>
                  <span style={{ display: "block", marginTop: 4, fontSize: 10, color: "#445566", fontWeight: 600 }}>{b.sub}</span>
                  <span style={{ display: "block", marginTop: 6, height: 14, background: b.led, borderRadius: 2 }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — utility panel (operator spec iter 75): UNIT STATUS,
            INCIDENT HISTORY, INCIDENT SUMMARY, HOUSE STATUS (SUPV),
            MSG TO DISP (SUPV), LOG OFF (red, replaces CLEAR FORM).

            Iter 81 — when the apparatus is not assigned to an incident,
            only UNIT STATUS / INCIDENT HISTORY / INCIDENT SUMMARY / LOG OFF
            stay enabled. HOUSE STATUS and MSG TO DISP are disabled (still
            visible to supervisors but unclickable) since they're situational
            actions that belong with an active dispatch context. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto" }}>
          {SIDE_PANEL.map((b) => {
            if (b.supvOnly && !isSupervisor) return null;
            const danger = !!b.danger;
            const noIncident = !unit?.current_job_id;
            const ALWAYS_ENABLED = ["unit_status", "incident_history", "incident_summary", "logoff"];
            const isDisabled = noIncident && !ALWAYS_ENABLED.includes(b.id);
            return (
              <button
                key={b.id}
                data-testid={`fire-mdt-side-${b.id.replace(/_/g, "-")}`}
                onClick={() => handleSidePanel(b.id)}
                disabled={isDisabled}
                style={{
                  ...UTIL_BTN_STYLE,
                  background: danger
                    ? "linear-gradient(180deg, #FF6B6B, #C42B2B)"
                    : UTIL_BTN_STYLE.background,
                  opacity: isDisabled ? 0.35 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  filter: isDisabled ? "grayscale(0.7)" : undefined,
                }}
              >
                <span style={{ fontWeight: 700, color: danger ? "#FFF" : "#202428" }}>{b.label}</span>
                <span style={{ display: "block", marginTop: 6, height: 12, background: danger ? "#FFF" : "#33CC33", borderRadius: 2 }} />
              </button>
            );
          })}
          <div style={{ marginTop: 8, padding: 8, background: "#FFF", border: "1px solid #444", fontSize: 12, color: "#000", lineHeight: 1.5 }}>
            <div><strong>10-8</strong> = Available</div>
            <div><strong>10-</strong> = Signal Activation</div>
          </div>
        </div>
      </div>

      {/* Footer — control buttons */}
      <div style={{ display: "flex", gap: 6, padding: 8, background: "#1A1E22" }}>
        <button data-testid="fire-mdt-send-btn" onClick={() => setStripText("SEND")} style={{ ...FOOTER_BTN_STYLE, background: "#D8252B", color: "#FFF" }}>SEND</button>
        {FOOTER_BUTTONS.map((label) => (
          <button
            key={label}
            data-testid={`fire-mdt-foot-${label.replace(/\s+/g, "_").toLowerCase()}`}
            onClick={() => setStripText(label)}
            style={FOOTER_BTN_STYLE}
          >
            {label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button data-testid="fire-mdt-switch" onClick={handleSwitchApparatus} style={{ ...FOOTER_BTN_STYLE, background: "#444", color: "#FFF" }}>
          SWITCH APPARATUS
        </button>
      </div>

      {/* Unit Status / Roster modal — shows the 5 fire crew positions
          and the 5 radio assignments. Built from the bound unit's
          unit.crew dict (set via VAC/VFD RCC logon). */}
      {showRoster && (
        <div
          data-testid="fire-mdt-roster-modal"
          onClick={() => setShowRoster(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 24, minWidth: 460, maxWidth: 600, fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1E5F9C" }}>UNIT STATUS</div>
                <div style={{ fontSize: 12, color: "#666" }}>{unit?.name} — {unit?.unit_type} ({unit?.agency})</div>
              </div>
              <button data-testid="fire-mdt-roster-close" onClick={() => setShowRoster(false)} style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>CLOSE</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#7A3A00", textTransform: "uppercase", letterSpacing: 1 }}>Crew Positions</div>
                {[
                  ["Chauffeur", roster?.chauffeur],
                  ["Officer",   roster?.officer],
                  ["Nozzle",    roster?.nozzle],
                  ["Backup",    roster?.backup],
                  ["Control",   roster?.control],
                ].map(([label, val], i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 14 }} data-testid={`fire-mdt-roster-${label.toLowerCase()}`}>
                    <span style={{ color: "#666", fontWeight: 600 }}>{label}</span>
                    <span style={{ color: val ? "#202428" : "#BBB", fontWeight: val ? 700 : 400 }}>{val || "—"}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#1E5F9C", textTransform: "uppercase", letterSpacing: 1 }}>Radios</div>
                {[1, 2, 3, 4, 5].map((n) => {
                  const v = (roster?.radios || [])[n - 1] || "";
                  return (
                    <div key={n} style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 14, fontFamily: "monospace" }} data-testid={`fire-mdt-roster-radio-${n}`}>
                      <span style={{ color: "#666", fontWeight: 600 }}>RADIO {n}</span>
                      <span style={{ color: v ? "#202428" : "#BBB", fontWeight: v ? 700 : 400 }}>{v || "—"}</span>
                    </div>
                  );
                })}
                <div style={{ marginTop: 16, padding: 8, background: "#F0F4F8", borderRadius: 3, fontSize: 12, color: "#555" }}>
                  <div><strong>Tour:</strong> {roster?.tour || "—"}</div>
                  <div><strong>Service:</strong> {unit?.service_time_from || "—"} → {unit?.service_time_to || "—"}</div>
                  <div><strong>Status:</strong> 10-{unit?.signal || "?"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signal Activation submenu — 10- expander on row 3.
          Stage 1 (primary): list of parent codes 10-25..10-43.
          Stage 2 (sub): NO CODE / CODE 1-4 for the picked parent.
          Parent codes with no `subs` POST immediately on click. */}
      {signalMenu && (
        <div
          data-testid="fire-mdt-signal-modal"
          onClick={() => setSignalMenu(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#1A1E22", color: "#FFF", borderRadius: 6, padding: 16, minWidth: 560, maxWidth: 720, maxHeight: "85vh", overflow: "auto", border: "2px solid #1E5F9C" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#FFD46B" }}>
                {signalMenu.stage === "primary" ? "SIGNAL ACTIVATION — pick code" : `${signalMenu.parent.code} — ${signalMenu.parent.desc}`}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {signalMenu.stage === "sub" && (
                  <button
                    data-testid="fire-mdt-signal-back"
                    onClick={() => setSignalMenu({ stage: "primary" })}
                    style={{ background: "#444", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}
                  >BACK</button>
                )}
                <button
                  data-testid="fire-mdt-signal-cancel"
                  onClick={() => setSignalMenu(null)}
                  style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}
                >CANCEL</button>
              </div>
            </div>

            {signalMenu.stage === "primary" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {SIGNAL_ACTIVATION_TREE.map((p) => (
                  <button
                    key={p.code}
                    data-testid={`fire-mdt-signal-parent-${p.code.replace(/-/g, "_")}`}
                    onClick={() => {
                      if (p.subs && p.subs.length) {
                        setSignalMenu({ stage: "sub", parent: p });
                      } else {
                        sendSignal(p.code, "", "");
                      }
                    }}
                    style={{
                      padding: "10px 8px",
                      background: "linear-gradient(180deg, #ECECEC, #C0C4CA)",
                      border: "1px solid #5A6068",
                      borderRadius: 4,
                      cursor: "pointer",
                      textAlign: "left",
                      color: "#202428",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {p.label}{(p.subs && p.subs.length) ? " ▸" : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#445566", marginTop: 2 }}>{p.desc}</div>
                  </button>
                ))}
              </div>
            )}

            {signalMenu.stage === "sub" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(signalMenu.parent.subs || []).map((s, i) => (
                  <button
                    key={i}
                    data-testid={`fire-mdt-signal-sub-${(s.code || "nocode").replace(/\s+/g, "_")}`}
                    onClick={() => sendSignal(signalMenu.parent.code, s.code, s.label)}
                    style={{
                      padding: "10px 12px",
                      background: "linear-gradient(180deg, #ECECEC, #C0C4CA)",
                      border: "1px solid #5A6068",
                      borderRadius: 4,
                      cursor: "pointer",
                      textAlign: "left",
                      color: "#202428",
                      fontWeight: 600,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* LOG OFF confirmation — operator spec iter 75 */}
      {confirmLogoff && (
        <div
          data-testid="fire-mdt-logoff-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
        >
          <div style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 24, minWidth: 380, fontFamily: "Arial, sans-serif", textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#C42B2B", marginBottom: 16 }}>LOG OFF</div>
            <div style={{ fontSize: 16, marginBottom: 24 }} data-testid="fire-mdt-logoff-prompt">
              Are you sure you want to log off?
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                data-testid="fire-mdt-logoff-yes"
                onClick={performLogoff}
                style={{ background: "#C42B2B", color: "#FFF", border: "none", padding: "10px 28px", borderRadius: 4, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >YES</button>
              <button
                data-testid="fire-mdt-logoff-no"
                onClick={() => setConfirmLogoff(false)}
                style={{ background: "#888", color: "#FFF", border: "none", padding: "10px 28px", borderRadius: 4, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >NO</button>
            </div>
          </div>
        </div>
      )}

      {/* INCIDENT SUMMARY modal — quick read of current job */}
      {showIncidentSummary && (
        <div
          data-testid="fire-mdt-incsum-modal"
          onClick={() => setShowIncidentSummary(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 24, minWidth: 480, maxWidth: 640, fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E5F9C" }}>INCIDENT SUMMARY</div>
              <button
                data-testid="fire-mdt-incsum-close"
                onClick={() => setShowIncidentSummary(false)}
                style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}
              >CLOSE</button>
            </div>
            {activeIncident ? (
              <div style={{ fontFamily: "monospace", fontSize: 13, lineHeight: 1.7 }}>
                <div><strong>INCIDENT#:</strong> {activeIncident.cad_number || activeIncident.fire_job_number || "—"}</div>
                <div><strong>CALL TYPE:</strong> {activeIncident.call_type || "—"}</div>
                <div><strong>PRIORITY:</strong> {activeIncident.priority || "—"}</div>
                <div><strong>BOX:</strong> {activeIncident.box || activeIncident.pct || "—"}</div>
                <div><strong>LOCATION:</strong> {activeIncident.location || ""}{activeIncident.apt ? ` APT ${activeIncident.apt}` : ""}</div>
                <div><strong>CROSS:</strong> {activeIncident.cross_low || "—"} / {activeIncident.cross_high || "—"}</div>
                <div><strong>STATUS:</strong> {activeIncident.status || "—"}</div>
                {activeIncident.cids_data && activeIncident.cids_data.length > 0 && (
                  <div style={{ marginTop: 12, padding: 8, background: "#FFF4E0", border: "1px solid #FFD46B", borderRadius: 3 }}>
                    <div style={{ fontWeight: 700, color: "#7A3A00" }}>CIDS</div>
                    {activeIncident.cids_data.map((c, i) => (<div key={i}>{c}</div>))}
                  </div>
                )}
                {activeIncident.location_data && activeIncident.location_data.length > 0 && (
                  <div style={{ marginTop: 8, padding: 8, background: "#F0F4F8", borderRadius: 3 }}>
                    <div style={{ fontWeight: 700, color: "#1E5F9C" }}>LOCATION DATA</div>
                    {activeIncident.location_data.map((c, i) => (<div key={i}>{c}</div>))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontStyle: "italic", color: "#888" }}>No active incident.</div>
            )}
          </div>
        </div>
      )}

      {/* INCIDENT HISTORY modal — current incident timeline (oldest top)
          + earlier incidents this tour (newest top) */}
      {showIncHistory && (
        <div
          data-testid="fire-mdt-inchist-modal"
          onClick={() => setShowIncHistory(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 20, minWidth: 560, maxWidth: 800, maxHeight: "85vh", overflow: "auto", fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E5F9C" }}>INCIDENT HISTORY — {unit?.name}</div>
              <button data-testid="fire-mdt-inchist-close" onClick={() => setShowIncHistory(false)} style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>CLOSE</button>
            </div>
            {incHistory ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, color: "#7A3A00", fontSize: 13, letterSpacing: 1, marginBottom: 6 }}>CURRENT INCIDENT (oldest → newest)</div>
                  {incHistory.current?.job ? (
                    <>
                      <div style={{ fontSize: 13, marginBottom: 6, color: "#444" }} data-testid="fire-mdt-inchist-current-job">
                        <strong>{incHistory.current.job.cad_number || incHistory.current.job.fire_job_number}</strong> — {incHistory.current.job.call_type || "—"} @ {incHistory.current.job.location || "—"}
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: 12, background: "#F5F7FA", padding: 8, borderRadius: 3, maxHeight: 200, overflow: "auto" }}>
                        {(incHistory.current.events || []).length === 0 && <div style={{ color: "#888" }}>No events yet</div>}
                        {(incHistory.current.events || []).map((ev) => (
                          <div key={ev.id} style={{ borderBottom: "1px solid #E0E4EC", padding: "3px 0" }}>
                            <span style={{ color: "#1E5F9C", fontWeight: 700 }}>{(ev.timestamp || "").slice(11, 19)}</span>{" "}
                            <span style={{ color: "#7A3A00", fontWeight: 700 }}>{(ev.event || "").toUpperCase()}</span>{" "}
                            <span style={{ color: "#444" }}>{ev.details || ""}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: "#888", fontStyle: "italic" }}>No active incident.</div>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "#7A3A00", fontSize: 13, letterSpacing: 1, marginBottom: 6 }}>EARLIER INCIDENTS (newest → oldest)</div>
                  {(incHistory.earlier || []).length === 0 ? (
                    <div style={{ color: "#888", fontStyle: "italic", fontSize: 13 }}>No earlier incidents on this apparatus.</div>
                  ) : (
                    <div style={{ fontSize: 12, fontFamily: "monospace" }}>
                      {(incHistory.earlier || []).map((j) => (
                        <div key={j.id} data-testid={`fire-mdt-inchist-earlier-${j.id}`} style={{ display: "grid", gridTemplateColumns: "100px 100px 1fr 90px", gap: 8, padding: "4px 6px", borderBottom: "1px solid #E0E4EC" }}>
                          <span style={{ color: "#1E5F9C", fontWeight: 700 }}>{j.fire_job_number || j.cad_number}</span>
                          <span style={{ color: "#7A3A00" }}>{j.call_type || "—"}</span>
                          <span>{j.location || "—"}</span>
                          <span style={{ color: "#888", textAlign: "right" }}>{(j.created_at || "").slice(11, 16)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ color: "#888" }}>Loading…</div>
            )}
          </div>
        </div>
      )}

      {/* HOUSE STATUS modal — firehouse occupancy at the parent agency */}
      {showHouseStatus && (
        <div
          data-testid="fire-mdt-house-modal"
          onClick={() => setShowHouseStatus(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 20, minWidth: 560, maxWidth: 800, maxHeight: "85vh", overflow: "auto", fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1E5F9C" }}>HOUSE STATUS</div>
                <div style={{ fontSize: 12, color: "#666" }}>{houseStatus?.agency || unit?.agency}</div>
              </div>
              <button data-testid="fire-mdt-house-close" onClick={() => setShowHouseStatus(false)} style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>CLOSE</button>
            </div>
            {houseStatus && !houseStatus.error ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                  <div style={{ padding: 10, background: "#E6F4EA", borderRadius: 3, textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#1B7A36" }} data-testid="fire-mdt-house-totals-in">{houseStatus.totals?.in_quarters ?? 0}</div>
                    <div style={{ fontSize: 11, color: "#1B7A36", fontWeight: 700, letterSpacing: 1 }}>IN QUARTERS</div>
                  </div>
                  <div style={{ padding: 10, background: "#FFF1E0", borderRadius: 3, textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#A04500" }} data-testid="fire-mdt-house-totals-out">{houseStatus.totals?.out_on_call ?? 0}</div>
                    <div style={{ fontSize: 11, color: "#A04500", fontWeight: 700, letterSpacing: 1 }}>OUT ON CALL</div>
                  </div>
                  <div style={{ padding: 10, background: "#EAEAEA", borderRadius: 3, textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#555" }} data-testid="fire-mdt-house-totals-off">{houseStatus.totals?.off_duty ?? 0}</div>
                    <div style={{ fontSize: 11, color: "#555", fontWeight: 700, letterSpacing: 1 }}>OFF DUTY</div>
                  </div>
                </div>
                {[
                  ["IN QUARTERS", "in_quarters", "#1B7A36"],
                  ["OUT ON CALL", "out_on_call", "#A04500"],
                  ["OFF DUTY", "off_duty", "#555"],
                ].map(([label, key, color]) => (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, color, fontSize: 13, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
                    {(houseStatus[key] || []).length === 0 ? (
                      <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>—</div>
                    ) : (
                      <div style={{ fontSize: 12, fontFamily: "monospace" }}>
                        {(houseStatus[key] || []).map((u) => (
                          <div key={u.id} data-testid={`fire-mdt-house-${key}-${u.name}`} style={{ display: "grid", gridTemplateColumns: "90px 110px 80px 1fr", gap: 8, padding: "4px 6px", borderBottom: "1px solid #E0E4EC" }}>
                            <span style={{ fontWeight: 700, color: "#1E5F9C" }}>{u.name}</span>
                            <span style={{ color: "#7A3A00" }}>{u.unit_type}</span>
                            <span style={{ color }}>10-{u.signal ?? "?"}</span>
                            <span style={{ color: "#444" }}>{(u.crew || []).join(", ") || "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div style={{ color: "#888" }}>{houseStatus?.error || "Loading…"}</div>
            )}
          </div>
        </div>
      )}

      {/* MSG TO DISP composer — fan-out via /api/messages */}
      {showMsgToDisp && (
        <div
          data-testid="fire-mdt-msg-modal"
          onClick={() => setShowMsgToDisp(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#FFF", color: "#202428", borderRadius: 6, padding: 20, minWidth: 460, maxWidth: 600, fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, borderBottom: "2px solid #1E5F9C", paddingBottom: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1E5F9C" }}>MSG TO DISP</div>
                <div style={{ fontSize: 12, color: "#666" }}>FROM: {unit?.name}</div>
              </div>
              <button data-testid="fire-mdt-msg-close" onClick={() => setShowMsgToDisp(false)} style={{ background: "#888", color: "#FFF", border: "none", padding: "6px 14px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>CLOSE</button>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 4 }}>RECIPIENTS</div>
              {msgRoster.length === 0 ? (
                <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>No online dispatchers</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 140, overflow: "auto", border: "1px solid #DDD", padding: 6, borderRadius: 3 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#1E5F9C" }}>
                    <input
                      data-testid="fire-mdt-msg-sendall"
                      type="checkbox"
                      checked={msgRecipients.includes("__ALL__")}
                      onChange={(e) => setMsgRecipients(e.target.checked ? ["__ALL__"] : [])}
                    />
                    SEND ALL ({msgRoster.length} online)
                  </label>
                  {msgRoster.map((d) => (
                    <label key={d.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, opacity: msgRecipients.includes("__ALL__") ? 0.5 : 1 }}>
                      <input
                        data-testid={`fire-mdt-msg-recip-${d.id}`}
                        type="checkbox"
                        disabled={msgRecipients.includes("__ALL__")}
                        checked={msgRecipients.includes(d.id)}
                        onChange={(e) => {
                          setMsgRecipients((cur) => e.target.checked ? [...cur.filter((x) => x !== "__ALL__"), d.id] : cur.filter((x) => x !== d.id));
                        }}
                      />
                      <span style={{ fontFamily: "monospace", color: "#1E5F9C", fontWeight: 700 }}>{d.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 4 }}>MESSAGE</div>
              <textarea
                data-testid="fire-mdt-msg-body"
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                rows={5}
                placeholder="Type your message…"
                style={{ width: "100%", padding: 8, fontSize: 13, fontFamily: "monospace", border: "1px solid #BBB", borderRadius: 3, resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
            {msgError && <div data-testid="fire-mdt-msg-error" style={{ color: "#C42B2B", fontSize: 12, marginBottom: 8 }}>{msgError}</div>}
            {msgSuccess && <div data-testid="fire-mdt-msg-success" style={{ color: "#1B7A36", fontSize: 12, marginBottom: 8 }}>{msgSuccess}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                data-testid="fire-mdt-msg-send"
                disabled={msgSending}
                onClick={sendMsgToDisp}
                style={{ background: msgSending ? "#888" : "#1E5F9C", color: "#FFF", border: "none", padding: "8px 22px", borderRadius: 3, fontWeight: 700, cursor: msgSending ? "default" : "pointer" }}
              >{msgSending ? "Sending…" : "SEND"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SHELL_STYLE = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: "#202428",
  color: "#FFF",
  fontFamily: "Arial, sans-serif",
};

const TITLE_BAR_STYLE = {
  background: "#1E5F9C",
  color: "#FFF",
  padding: "6px 12px",
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 1,
};

const INCIDENT_CARD_STYLE = {
  flex: 1,
  background: "#1E5F9C",
  color: "#FFF",
  padding: 18,
  fontSize: 18,
  fontFamily: "Courier New, monospace",
  fontWeight: 700,
  borderRadius: 4,
  overflow: "auto",
};

const STATUS_STRIP_STYLE = {
  background: "#000",
  height: 36,
  padding: "0 12px",
  display: "flex",
  alignItems: "center",
  border: "1px solid #444",
  borderRadius: 2,
};

const GRID_BTN_STYLE = {
  padding: "12px 6px",
  background: "linear-gradient(180deg, #ECECEC, #C0C4CA)",
  border: "1px solid #5A6068",
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "Arial, sans-serif",
  textAlign: "center",
};

const UTIL_BTN_STYLE = {
  padding: "10px 6px",
  background: "linear-gradient(180deg, #ECECEC, #C0C4CA)",
  border: "1px solid #5A6068",
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "Arial, sans-serif",
  textAlign: "center",
  minHeight: 56,
};

const FOOTER_BTN_STYLE = {
  padding: "8px 14px",
  background: "linear-gradient(180deg, #ECECEC, #C0C4CA)",
  border: "1px solid #5A6068",
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "Arial, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  color: "#202428",
};
